/**
 * Аккаунты набора: выбор цели команды и подписи.
 *
 * Модуль ЧИСТЫЙ: ни сети, ни хранилища. Опрос состава живёт в
 * store/useAccounts.ts, выбор оператора — в настройках (lib/settings.ts).
 *
 * Главное правило (инвариант 4 roadmap-accounts): умолчания «единственный
 * аккаунт» нет нигде. Выбор запоминается в настройках, но команда уходит,
 * только когда он сделан и всё ещё в составе, — иначе второй аккаунт,
 * появившись в наборе, молча унаследовал бы чужие нажатия.
 */

import type { AccountCohort, AccountRow, AccountsResponse, CommandTarget } from "../api/types";

/** Выбор оператора, как он хранится в настройках: аккаунт или когорта. */
export type TargetChoice =
  | {
      kind: "account";
      /** Funder, нижний регистр. */
      funder: string;
    }
  | {
      kind: "cohort";
      /** Имя когорты (T5.5). */
      name: string;
    };

const COHORT_RE = /^[a-z0-9_-]{1,32}$/;

/** Подходит ли строка как имя когорты: то же правило, что у web_api. */
export function isCohortName(raw: unknown): raw is string {
  return typeof raw === "string" && COHORT_RE.test(raw);
}

const FUNDER_RE = /^0x[0-9a-f]{40}$/;

/** Подходит ли строка как funder: та же форма, что account.ParseID в Go. */
export function isFunder(raw: unknown): raw is string {
  return typeof raw === "string" && FUNDER_RE.test(raw);
}

/**
 * Разбор сохранённого выбора. Всё непонятное — «не выбран», а не попытка
 * угадать: выбор, восстановленный неверно, — это ордер не тем аккаунтом.
 */
export function decodeTarget(raw: unknown): TargetChoice | null {
  if (typeof raw !== "object" || raw === null) return null;
  const bag = raw as Record<string, unknown>;
  if (bag["kind"] === "cohort") {
    return isCohortName(bag["name"]) ? { kind: "cohort", name: bag["name"] } : null;
  }
  if (bag["kind"] !== "account") return null;
  const funder = typeof bag["funder"] === "string" ? bag["funder"].toLowerCase() : null;
  return isFunder(funder) ? { kind: "account", funder } : null;
}

/** Строка состава по funder'у; регистр адреса не важен. */
export function accountOf(
  accounts: AccountsResponse | null | undefined,
  funder: string | null | undefined,
): AccountRow | null {
  if (!accounts || !funder) return null;
  const want = funder.toLowerCase();
  return accounts.accounts.find((a) => a.funder === want) ?? null;
}

/** Слот для показа: столбец «аккаунт» в ордерах и ленте. */
export function slotOf(
  accounts: AccountsResponse | null | undefined,
  funder: string | null | undefined,
): string | null {
  return accountOf(accounts, funder)?.slot ?? null;
}

/** Короткая форма адреса, когда слота нет (аккаунт выпал из состава). */
export function shortFunder(funder: string): string {
  return funder.length > 10 ? `${funder.slice(0, 6)}…${funder.slice(-4)}` : funder;
}

/** Подпись аккаунта в столбцах: слот, иначе короткий адрес, иначе прочерк. */
export function accountLabel(
  accounts: AccountsResponse | null | undefined,
  funder: string | null | undefined,
): string {
  if (!funder) return "—";
  return slotOf(accounts, funder) ?? shortFunder(funder);
}

/**
 * Чем аккаунт ограничен сейчас; null — торгует без ограничений.
 *
 * Применённое состояние важнее заявленного: торгует то, что применил трейдер.
 * Неизвестное применение — не запрет: решает трейдер, а панель только
 * подсказывает.
 */
export function accountHint(row: AccountRow): string | null {
  // Заявленное осушение называется и у незагруженного: иначе B01 из резерва
  // и выводимый B01 выглядели бы в списке одинаково.
  if (row.loaded === false) {
    return row.state === "enabled" ? "не загружен трейдером" : "не загружен трейдером, осушается";
  }
  const state = row.state_applied ?? row.state;
  if (state !== "enabled") return "осушается: только продажа и снятие";
  if (row.loaded === null) return "применение неизвестно";
  return null;
}

export type ResolvedTarget =
  | { target: CommandTarget; account: string; cohort: null; problem: null }
  | { target: CommandTarget; account: null; cohort: string; problem: null }
  | { target: null; account: null; cohort: null; problem: string };

/**
 * Цель команды из выбора оператора и состава.
 *
 * Неизвестный состав (нет sidecar'а и трейдер молчит) команду НЕ блокирует:
 * web_api её опубликует, а трейдер, единственный, кто знает загруженное,
 * откажет с причиной. Запрещать торговлю панелью из-за того, что web_api
 * чего-то не знает, хуже.
 *
 * Когорта проверяется только на существование: годных в ней считает web_api
 * при раскрытии (и отвечает 409, если их нет), а панель показывает это
 * предпросмотром (cohortPreview), не запретом — «снять все» когортой законно и
 * на осушаемых.
 */
export function resolveTarget(
  choice: TargetChoice | null,
  accounts: AccountsResponse | null | undefined,
  cohorts?: AccountCohort[] | null,
): ResolvedTarget {
  const none = (problem: string): ResolvedTarget => ({
    target: null,
    account: null,
    cohort: null,
    problem,
  });
  if (!choice) return none("выберите аккаунт");
  if (choice.kind === "cohort") {
    if (cohorts && !cohorts.some((c) => c.name === choice.name)) {
      return none(`когорты «${choice.name}» больше нет — выберите другую цель`);
    }
    return {
      target: { account_cohort: choice.name },
      account: null,
      cohort: choice.name,
      problem: null,
    };
  }
  if (accounts?.known && !accountOf(accounts, choice.funder)) {
    return none("выбранный аккаунт выпал из состава — выберите другой");
  }
  return {
    target: { account: choice.funder },
    account: choice.funder,
    cohort: null,
    problem: null,
  };
}

/**
 * Почему от этой цели нельзя взять сигнал; null — можно.
 *
 * Сигнал берётся только аккаунтом: он забирается один раз, и команды когорты
 * подрались бы за него сами с собой (web_api отвечает 400).
 */
export function takeProblem(r: ResolvedTarget): string | null {
  if (r.account) return null;
  if (r.cohort) return "сигнал берётся только аккаунтом — выберите аккаунт, а не когорту";
  return r.problem;
}

/**
 * Почему данные одного аккаунта (позиция, тир) не запрашиваются; null —
 * аккаунт выбран.
 *
 * Отдельно от takeProblem, хотя правило то же: там отказ команды, здесь —
 * молчащий раздел. Без причины для когорты раздел показывал бы «загрузку»
 * вечно: запрос выключен, а цель при этом годная.
 */
export function accountOnlyProblem(r: ResolvedTarget, what: string): string | null {
  if (r.account) return null;
  if (r.cohort) return `${what} — у каждого аккаунта свой: выбрана когорта, выберите аккаунт`;
  return r.problem;
}

/**
 * Годится ли аккаунт для постановки когортой — то же правило, что у web_api
 * (accountcohorts.go, eligible): загружен и применённо enabled; без ответа
 * трейдера — по заявленному state.
 */
export function placeEligible(row: AccountRow): boolean {
  if (row.loaded !== null) return row.loaded && row.state_applied === "enabled";
  return row.state === "enabled";
}

/** «1 аккаунт», «3 аккаунта», «5 аккаунтов». */
export function accountsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} аккаунт`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} аккаунта`;
  return `${n} аккаунтов`;
}

export interface CohortPreview {
  /** Годные к постановке — им уйдёт по команде полного размера. */
  eligible: AccountRow[];
  /** Пропущенные и почему: оператор видит, кого когорта не охватит. */
  skipped: { label: string; reason: string }[];
  /** «3 аккаунта × $25»; без суммы — только число аккаунтов. */
  text: string;
}

/**
 * Итог постановки когортой до отправки (Д-5, режим each): каждый годный
 * аккаунт ставит ПОЛНЫЙ размер. Предпросмотр нужен ровно потому, что «$25»
 * на кнопке при когорте из трёх — это $75 риска.
 */
export function cohortPreview(
  cohort: AccountCohort,
  accounts: AccountsResponse | null | undefined,
  usd?: string | null,
): CohortPreview {
  const eligible: AccountRow[] = [];
  const skipped: { label: string; reason: string }[] = [];
  for (const funder of cohort.accounts) {
    const row = accountOf(accounts, funder);
    if (!row) {
      skipped.push({ label: shortFunder(funder), reason: "вне состава" });
    } else if (placeEligible(row)) {
      eligible.push(row);
    } else {
      skipped.push({ label: row.slot, reason: accountHint(row) ?? "не годен" });
    }
  }
  const n = accountsWord(eligible.length);
  return { eligible, skipped, text: usd ? `${n} × $${usd}` : n };
}

/**
 * Что мешает сохранить черновик; null — можно. Те же правила, что у web_api
 * (accountcohorts.go): проверка здесь — только чтобы не отправлять заведомый
 * отказ, решает всё равно сервер.
 */
export function draftProblem(draft: Pick<AccountCohort, "name" | "accounts">[]): string | null {
  const seen = new Set<string>();
  for (const c of draft) {
    if (!isCohortName(c.name)) return `имя «${c.name}»: 1–32 символа из a-z, 0-9, _ и -`;
    if (seen.has(c.name)) return `имя «${c.name}» повторяется`;
    seen.add(c.name);
    if (c.accounts.length === 0) return `в когорте «${c.name}» нет аккаунтов`;
  }
  return null;
}

/**
 * Хвост эха об отказах части когорты; пусто — ушли все. Без него «отправлено»
 * над когортой из трёх, где ушли две команды, было бы неправдой о третьем
 * аккаунте.
 */
export function partialNote(partial: string[]): string {
  return partial.length > 0 ? ` · не ушло: ${partial.join("; ")}` : "";
}
